import { z } from "zod";

/**
 * Filtro de fechas del listado de consultas: esquema de la URL y su traducción a lo que se
 * pinta en pantalla.
 *
 * Vive en `lib/` y no junto a la ruta porque así se puede probar con `node --test`: el árbol de
 * `api/` arrastra `config/env` con `import.meta.env`, que fuera de Vite no existe. Es la misma
 * razón por la que `inquiry-criteria.ts` está aquí.
 */

/** Un día en el formato que viaja en la URL. */
const DIA = /^\d{4}-\d{2}-\d{2}$/;

export const consultasSearchSchema = z.object({
  // `YYYY-MM-DD` en la URL (legible y compartible); a ISO al llamar a la API. Mismo formato y
  // mismos nombres que el listado de bajas, para que las tres pantallas se comporten igual.
  //
  // El `regex` NO es decorativo: `.catch()` solo atrapa lo que el esquema rechaza, y un
  // `z.string()` acepta `?from=abc` tal cual. Ese valor llegaría al date picker, que lo formatea
  // con date-fns v4 y ahí sí truena (`Invalid time value`), o sea pantalla en blanco. Con el
  // formato validado, la basura degrada al default como cualquier otro valor inservible.
  from: z.string().regex(DIA).optional().catch(undefined),
  to: z.string().regex(DIA).optional().catch(undefined),
  // Atajo de "todo un año". Se conserva porque puede haber enlaces guardados con `?year=`.
  //
  // `.catch(undefined)` en los tres: un valor pegado a mano degrada a la vista por default en
  // vez de reventar `validateSearch` con un ZodError y dejar la pantalla en blanco. Mismo
  // criterio que el `?view=` de la pantalla de boletas.
  year: z.number().int().min(2010).max(2100).optional().catch(undefined),
  // Estados separados por coma, como en inscripciones y bajas: los chips son
  // multi-selección y el filtro tiene que caber en un enlace compartible. No se valida el
  // contenido aquí porque el catálogo de estados vive en `api/inquiries`, y ese árbol
  // arrastra `import.meta.env`; el backend descarta lo desconocido de todas formas.
  status: z.string().optional().catch(undefined),
});

export type ConsultasSearch = z.infer<typeof consultasSearchSchema>;

/**
 * El rango que el date picker debe MOSTRAR.
 *
 * `?year=` es el mismo filtro escrito de otra forma, así que se pinta como el rango que es.
 * Sin esto, un enlace viejo deja el picker en blanco mientras la lista sí viene filtrada, y el
 * mensaje de vacío culpa al catálogo ("todavía no hay consultas") en vez del filtro puesto.
 */
export function rangoVisibleDe(search: ConsultasSearch): {
  from: string | undefined;
  to: string | undefined;
} {
  if (search.from || search.to) return { from: search.from, to: search.to };
  if (search.year !== undefined) {
    return { from: `${search.year}-01-01`, to: `${search.year}-12-31` };
  }
  return { from: undefined, to: undefined };
}

/** Los estados seleccionados, como lista. Vacía si no hay filtro. */
export function estadosDe(search: { status?: string }): string[] {
  return search.status ? search.status.split(",").filter(Boolean) : [];
}

/** El valor que va a la URL: `undefined` cuando no queda ninguno, para no dejar `?status=`. */
export function estadosAUrl(estados: string[]): string | undefined {
  return estados.length ? estados.join(",") : undefined;
}
